<template>
  <div class="rc-page">
    <!-- Hero banner -->
    <page-hero
        class="mb-1"
        :title="msg('Rent contracts')"
        :subtitle="msg('Manage reservations and bookings')"
        variant="contract"
        :stats="[{ icon: 'FileTextIcon', value: page.total, label: msg('total') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="15" y="8" width="70" height="95" rx="6" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
          <rect x="28" y="24" width="32" height="3" rx="1.5" fill="rgba(255,255,255,.4)"/>
          <rect x="28" y="34" width="44" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
          <rect x="28" y="42" width="38" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
          <line x1="28" y1="58" x2="72" y2="58" stroke="rgba(255,255,255,.2)" stroke-width="1" stroke-dasharray="3 2"/>
          <rect x="28" y="68" width="24" height="8" rx="4" fill="rgba(255,255,255,.3)"/>
          <circle cx="82" cy="25" r="8" fill="rgba(255,255,255,.08)"/>
          <circle cx="8" cy="85" r="10" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <!-- Action buttons (moved from header) -->
    <div class="d-flex align-items-center flex-wrap mb-1" style="gap:8px">
      <b-button variant="outline-danger" class="rc-btn" size="sm" @click="onBlockCalendar()">
        <i class="fas fa-ban mr-50"></i> {{ msg('blockCalendar') }}
      </b-button>
      <b-button :disabled="isApaleoBranchOnly" variant="primary" class="rc-btn" size="sm" @click="onTreasureContractGenerateMulti()">
        <i class="fas fa-plus mr-50"></i> {{ msg('Create contract') }}
      </b-button>
      <b-button variant="outline-primary" class="rc-btn" size="sm" @click="refresh()">
        <i class="fas fa-sync-alt mr-50"></i> {{ msg('Reload') }}
      </b-button>
    </div>

    <!-- Filters -->
    <div class="rc-filter-wrap" v-bind:class="{'sticky':stickyFilter}">
      <b-card no-body class="rc-filter-card">
        <b-card-header class="py-75 px-2">
          <div class="d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center">
              <i class="fas fa-filter rc-filter-icon mr-75"></i>
              <span class="rc-filter-title">{{ msg("Filters") }}</span>
            </div>
            <div class="d-flex align-items-center" style="gap:10px">
              <b-link v-bind:class="{'rc-pin-active':stickyFilter, 'rc-pin':!stickyFilter}" @click="stickyFilter = !stickyFilter">
                <i class="fa-solid fa-thumbtack"></i>
              </b-link>
              <b-link class="rc-filter-action" @click="resetFilters">
                <i class="fas fa-times"></i>
              </b-link>
              <b-link class="rc-filter-action" @click="refresh()">
                <i class="fas fa-redo"></i>
              </b-link>
            </div>
          </div>
        </b-card-header>
        <b-card-body class="pt-75 pb-1">
          <b-row class="rc-filter-grid">
            <b-col cols="6" md="3" class="rc-filter-col">
              <label class="rc-label">{{ msg("Id") }}</label>
              <b-form-input size="sm" v-model="filter.like.id" class="rc-input" :placeholder="msg('Search by ID')" />
            </b-col>
            <b-col cols="6" md="3" class="rc-filter-col">
              <label class="rc-label">{{ msg('Status') }}</label>
              <v-select multiple :dir="isRTL ? 'rtl' : 'ltr'" :clearable="true" v-model="filter.in.status" :options="allContractStatus" class="rc-select small w-100" label="text" :reduce="(s) => s.value" />
            </b-col>
            <b-col cols="6" md="3" class="rc-filter-col">
              <label class="rc-label">{{ msg('EndDate') }}</label>
              <v-select :dir="isRTL ? 'rtl' : 'ltr'" :clearable="true" v-model="temp" :options="[{value:getUnderYear,label:msg('Under 1 year')},{value:getBetween1And2,label:msg('Between 1 and 2 years')},{value:over2Years,label:msg('Over 2 years')}]" class="rc-select w-100 small" label="label" :reduce="(v) => v.value" />
            </b-col>
            <b-col cols="6" md="3" class="rc-filter-col">
              <label class="rc-label">{{ msg('Platform') }}</label>
              <v-select :dir="isRTL ? 'rtl' : 'ltr'" v-model="filter.eq.bnbStyleRent.platform" :options="[{value:'Booking',label:'Booking'},{value:'Airbnb',label:'Airbnb'},{value:'Treasure Home',label:'Treasure Home'},{value:'Expedia',label:'Expedia'},{value:'Vrbo',label:'Vrbo'},{value:'Agoda',label:'Agoda'},{value:null,label:'All'}]" :clearable="false" label="label" :reduce="(s) => s.value" class="rc-select w-100 small" />
            </b-col>
            <b-col cols="12" md="3" class="rc-filter-col">
              <label class="rc-label">{{ $t('Real estate') }}</label>
              <real-estate-picker small v-model="filter.eq.propertyId"/>
            </b-col>
            <b-col cols="6" md="2" class="rc-filter-col">
              <label class="rc-label">{{ msg("Firstname") }}</label>
              <b-form-input size="sm" v-model="filter.eq.client.firstname" class="rc-input" />
            </b-col>
            <b-col cols="6" md="2" class="rc-filter-col">
              <label class="rc-label">{{ msg("Lastname") }}</label>
              <b-form-input size="sm" v-model="filter.eq.client.lastname" class="rc-input" />
            </b-col>
            <b-col cols="6" md="2" class="rc-filter-col">
              <label class="rc-label">{{ msg("Email") }}</label>
              <b-form-input size="sm" v-model="filter.eq.client.email" class="rc-input" />
            </b-col>
            <b-col cols="6" md="3" class="rc-filter-col">
              <label class="rc-label">{{ msg("External id") }}</label>
              <b-form-input size="sm" v-model="filter.like.platformReservationId" class="rc-input" />
            </b-col>
            <b-col cols="12" md="5" class="rc-filter-col">
              <label class="rc-label">{{ msg('checkinrange') }}</label>
              <calendar-picker-range size="sm" v-model="datesRange"/>
            </b-col>
            <b-col cols="12" md="2" class="rc-filter-col d-flex align-items-end">
              <b-button variant="outline-danger" size="sm" class="rc-btn w-100" @click="datesRange=null">
                <i class="fas fa-times mr-50"></i> {{ msg('Reset') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </div>

    <!-- Table Card -->
    <b-card no-body class="rc-table-card">
      <b-card-body>
        <!-- Table Controls -->
        <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap">
          <div class="d-flex align-items-center">
            <label class="rc-label mb-0 mr-50">{{ msg('Show') }}</label>
            <v-select
                v-model="page.amount"
                :dir="isRTL ? 'rtl' : 'ltr'"
                :options="[10, 25, 50, 100]"
                :clearable="false"
                class="rc-page-select d-inline-block"
                @input="onPerPageChange"
            />
            <label class="rc-label mb-0 ml-50">{{ msg('entries') }}</label>
          </div>
          <div class="rc-total-badge">
            <i class="fas fa-list-ol mr-50"></i>
            <strong>{{ msg('Total') }}:</strong> {{ page.total }}
          </div>
        </div>

        <!-- Table -->
        <div v-handy-scroll class="table-responsive">
          <b-table
              ref="contracts-table"
              striped
              hover
              responsive
              v-bind:items="contracts"
              v-bind:fields="fields"
              :busy="loading"
              @sort-changed="onSort"
              @row-clicked="onRowClick"
              class="rc-table"
              thead-class="rc-thead"
              tbody-tr-class="rc-row"
          >
            <template #table-busy>
              <div class="text-center text-primary my-3">
                <b-spinner class="align-middle mx-1"></b-spinner>
                <strong>{{ msg('Loading') }}...</strong>
              </div>
            </template>

            <template #cell(status)="data">
              <b-badge pill :variant="getStatusVariant(data.value)" class="rc-status-badge">
                <i :class="getStatusIcon(data.value)" class="mr-25"></i>
                {{ msg(data.value) }}
              </b-badge>
            </template>

            <template #cell(id)="data">
              <b-button variant="light" size="sm" class="rc-view-btn" @click.stop="openSideBar(data.value)" v-b-tooltip.hover :title="msg('Quick view')">
                <i class="fas fa-eye"></i>
              </b-button>
            </template>

            <template #cell(creationTime)="data">
              <span class="rc-date-text">{{ $moment(data.value).format('DD/MM/YY HH:mm') }}</span>
            </template>

            <template #cell(propertyId)="data">
              <div v-if="realEstates[data.value] != null" class="d-flex align-items-center">
                <b-img
                    v-bind="{ blank: (realEstates[data.value].attachments == null || realEstates[data.value].attachments[0] == null), blankColor: '#777', width: 42, height: 42 }"
                    v-bind:src="getImageSrc(realEstates[data.value])"
                    blank-color="#475F7B"
                    rounded
                    alt="Property"
                    class="rc-property-img mr-75"
                />
                <div class="rc-property-text">
                  <div class="rc-property-addr">{{ realEstates[data.value].address }} {{ realEstates[data.value].house }}/{{ realEstates[data.value].apt }}</div>
                  <div class="rc-property-city">{{ realEstates[data.value].city }}</div>
                </div>
              </div>
            </template>

            <template #cell(bnbStyleRent)="data">
              <platform-logo v-if="data.value.platform != null" v-bind:platform="data.value.platform">
                {{ data.value.platform }}
              </platform-logo>
            </template>

            <template #cell(client)="data">
              <div class="rc-client-cell">
                <i class="fas fa-user-circle rc-client-icon mr-50"></i>
                <div>
                  <div class="rc-client-name">{{ data.value.firstname }} {{ data.value.lastname }}</div>
                  <div class="rc-client-country">{{ data.value.address.country }}</div>
                </div>
              </div>
            </template>

            <template #cell(moveDate)="data">
              <div class="d-flex align-items-center" style="gap:6px">
                <span v-if="data.item.moveDate && data.item.moveDate !== ''" class="rc-date-pill rc-date-checkin">
                  <i class="fas fa-sign-in-alt mr-25"></i>{{ $moment(data.item.moveDate).format('DD.MM.YY') }}
                </span>
                <span v-else class="text-muted" style="font-size:11px">-</span>
                <i class="fas fa-arrow-right" style="font-size:9px;opacity:.4"></i>
                <span v-if="data.item.endDate && data.item.endDate !== ''" class="rc-date-pill" :class="'rc-date-' + getEndDateColor(data.item.endDate)">
                  <i class="fas fa-sign-out-alt mr-25"></i>{{ $moment(data.item.endDate).format('DD.MM.YY') }}
                </span>
                <span v-else class="text-muted" style="font-size:11px">-</span>
              </div>
            </template>

            <template #cell(alerts)="data">
              <div v-if="data.value != null" class="rc-progress-wrap">
                <b-progress height="8px" :variant="variantColorByProgress[getProgressValue(data.item)]" :value="getProgressValue(data.item)" :max="100" class="rc-progress"></b-progress>
                <span class="rc-progress-label">{{ getProgressValue(data.item) }}%</span>
              </div>
            </template>

            <template #cell(price)="data">
              <span class="rc-price" v-if="data.item.bnbStyleRent!=null">{{ convertToEURO(data.item.bnbStyleRent.totalPayment) }}</span>
              <span class="rc-price" v-else>{{ data.value }}</span>
            </template>

            <template #cell(active)="data">
              <span v-if="data.item.allowEarlyCheckIn === true" class="rc-special-badge rc-special-yes">
                <i class="fas fa-clock mr-25"></i>Early
              </span>
              <span v-else-if="data.item.allowLateCheckOut === true" class="rc-special-badge rc-special-yes">
                <i class="fas fa-moon mr-25"></i>Late
              </span>
              <span v-else class="rc-special-badge rc-special-no">
                <i class="fas fa-minus"></i>
              </span>
            </template>

            <template #cell(depositPaid)="data">
              <i :class="data.value ? 'fas fa-check-circle rc-check-yes' : 'fas fa-times-circle rc-check-no'"></i>
            </template>

            <template #cell(depositRefunded)="data">
              <i :class="data.value ? 'fas fa-check-circle rc-check-yes' : 'fas fa-times-circle rc-check-no'"></i>
            </template>
          </b-table>
        </div>

        <!-- Pagination -->
        <div class="d-flex align-items-center justify-content-between mt-2 flex-wrap">
          <div class="rc-page-info text-muted">
            <strong>{{ msg('Total') }}:</strong> {{ page.total }}
          </div>
          <b-pagination
              v-model="page.currentPage"
              :total-rows="page.total"
              :per-page="page.amount"
              first-number
              last-number
              @input="refresh"
              class="rc-pagination mb-0"
          />
        </div>
      </b-card-body>
    </b-card>

    <!-- Contract Rent Panel Modal -->
    <b-modal no-fade v-model="dialogs.contract" size="xl" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <contract-rent-panel ref="realEstatePanel" @submitted="onSubmitted"/>
      <div class="text-center mt-2">
        <b-button :disabled="savingContract" variant="success" class="rc-btn mr-1" @click="submitContract">
          <i class="fas fa-save mr-50"></i> {{ savingContract ? msg('Loading') : msg('Save') }}
        </b-button>
        <b-button :disabled="!savingContract" variant="outline-danger" class="rc-btn" @click="resetButton">
          <i class="fas fa-undo mr-50"></i> {{ msg('Reset') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Busy Contracts Modal -->
    <b-modal ref="contractModal" :title="msg('Contracts')" ok-only centered content-class="rc-modal-content">
      <ul v-if="busyContracts!=null && busyContracts['id']!=null && busyContracts['id'].length>0" class="rc-busy-list">
        <li v-for="contract in busyContracts.server" :key="contract.id" class="rc-busy-item">
          <b-link @click="goToContract(contract.id)" class="font-weight-bold">{{ msg('To the contract') }}</b-link>
          <div class="text-muted mt-25">{{ contract.propertyAddress }}</div>
          <div>{{ contract.client.firstname + ' ' + contract.client.lastname }}</div>
          <div class="text-muted">{{ $moment(contract.bnbStyleRent.start, 'YYYY-MM-DD').format("DD/MM/YYYY") + ' - ' + $moment(contract.bnbStyleRent.end, 'YYYY-MM-DD').format("DD/MM/YYYY") }}</div>
        </li>
      </ul>
      <p v-else class="text-muted text-center my-2">{{ msg('No active bookings for given dates') }}</p>
    </b-modal>

    <!-- Treasure Contract Panel Modal -->
    <b-modal modal-class="modal-fullscreen" no-fade v-model="dialogs.treasureContract" size="xl" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <treasure-contract-rent-panel ref="realEstatePanel" @submitted="onSubmitted"/>
      <div class="text-center mt-2">
        <b-button :disabled="savingContract" variant="success" class="rc-btn mr-1" @click="submitContract">
          <i class="fas fa-save mr-50"></i> {{ savingContract ? msg('Loading') : msg('Save') }}
        </b-button>
        <b-button :disabled="!savingContract" variant="outline-danger" class="rc-btn" @click="resetButton">
          <i class="fas fa-undo mr-50"></i> {{ msg('Reset') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Treasure Contract Multi Modal -->
    <b-modal no-fade v-model="dialogs.treasureContractMulti" size="lg" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <treasure-contract-rent-panel-multi ref="realEstatePanel" @submitted="onSubmitted" @checked="contractToast"/>
    </b-modal>

    <!-- Block Calendar Modal -->
    <b-modal modal-class="modal-fullscreen" no-fade v-model="dialogs.blockCalendar" size="lg" :hide-footer="true" no-close-on-backdrop centered content-class="rc-modal-content">
      <block-dates-panel ref="blockPanel" @submitted="onSubmitted"/>
    </b-modal>

    <ContractRentSidebar ref="contractRentSidebar"/>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ContractRentPanel from "@/views/realEstate/contracts/contractRentPanel";
import TreasureContractRentPanel from "@/views/realEstate/contracts/treasureContractRentPanel";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker";
import CalendarPickerRange from "@/components/widgets/calendar/calendarPickerRange";
import PlatformLogo from "@/components/widgets/PlatformLogo";
import TreasureContractRentPanelMulti from "@/views/realEstate/contracts/treasureContractRentPanelMulti";
import BlockDatesPanel from "@/views/realEstate/contracts/blockDatesPanel.vue";
import mixinFilter from "@/mixins/mixinFilter";
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import ContractRentSidebar from "@/components/sidebar/ContractRent/ContractRentSidebar.vue";
import PageHero from "@/components/PageHero.vue";

const rentContractFilter = () => ({
  like: {search: '', name: "", shortDescription: ""},
  eq: {type: "", client: {}, over30Days: false, bnbStyleRent: {platform: null}},
  lte: {}, gte: {}, in: {}
});

export default {
  name: "RealEstateRentContracts",
  mixins: [mixinFilter],
  components: {
    PageHero,
    ContractRentSidebar,
    BlockDatesPanel,
    ContractRentPanel,
    TreasureContractRentPanel,
    TreasureContractRentPanelMulti,
    RealEstatePicker,
    CalendarPickerRange,
    PlatformLogo
  },
  data() {
    return {
      datesRange: null,
      loading: true,
      busyContracts: null,
      savingContract: false,
      amount: 10,
      contracts: [],
      realEstates: {},
      dialogs: {contract: false, treasureContract: false, treasureContractMulti: false, blockCalendar: false},
      temp: {},
      filter: rentContractFilter(),
      stickyFilter: false,
      sort: {by: 'creationTime', desc: true, direction: 'desc'},
      page: {amount: 10, currentPage: 1, total: 0},
      listeners: {
        refresh: () => {
        }
      }
    }
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.refresh()
      }
    },
    datesRange: {
      handler() {
        this.refresh()
      }
    },
  },
  created() {
    let $this = this;
    this.filter = this.loadCachedFilters(rentContractFilter);
    this.refresh()
    this.$root.$on('crm::branch-updated', (this.listeners.refresh = () => {
      $this.refresh()
    }))
  },
  computed: {
    ...mapGetters('data', ['getOrganizationWebSite', 'getRealEstatesWebSite']),
    ...mapGetters('realEstateContracts', ['allStatus']),
    ...mapGetters('branches', ['allBranches']),
    isApaleoBranchOnly() {
      return this.allBranches.length > 0 && this.allBranches.every(branch => branch.name === 'APALEO');
    },
    allContractStatus() {
      return this.allStatus.map(status => ({
        value: status.value,
        text: this.msg(status.text)
      }))
    },
    getUnderYear() {
      return this.$moment().add(1, 'year').format('YYYY-MM-DD');
    },
    getBetween1And2() {
      return this.$moment().add(1, 'year').format('YYYY-MM-DD');
    },
    over2Years() {
      return this.$moment().add(1, 'year').format('YYYY-MM-DD');
    },
    variantColorByProgress() {
      return {
        100: 'success',
        75: 'warning',
        50: 'warning',
        25: 'danger',
        0: 'danger'
      };
    },
    fields() {
      let $this = this
      return [
        {key: 'status', label: 'Status', sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'id', label: 'View', sortable: true, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
        {key: 'creationTime', label: $this.msg('Creation Time'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'propertyId', label: $this.msg('Address'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'bnbStyleRent', label: $this.msg('Platform'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'platformReservationId', label: $this.msg('Platform Id'), sortable: true, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'client', label: $this.msg('Client'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'alerts', label: $this.msg('Progress'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'price', label: $this.msg('Rental price'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'moveDate', label: $this.msg('CheckIn - CheckOut'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'active', label: $this.msg('Special requests'), sortable: false, thClass: 'rc-th', tdClass: 'rc-td'},
        {key: 'numberOfGuests', label: $this.msg('numberOfGuests'), sortable: false, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
        {key: 'depositPaid', label: $this.msg('Deposit paid'), sortable: true, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
        {key: 'depositRefunded', label: $this.msg('Deposit refunded'), sortable: true, thClass: 'rc-th rc-th-center', tdClass: 'rc-td text-center'},
      ]
    },
  },
  methods: {
    ...mapActions('realEstateContracts', ['getAllContracts', 'sendSMSCheckIn']),
    getStatusVariant(status) {
      const map = { ACTIVE: 'success', CANCELED: 'danger', REVIEWING: 'secondary', NEW: 'warning', INQUIRY: 'warning' }
      return map[status] || 'info'
    },
    getStatusIcon(status) {
      const map = { ACTIVE: 'fas fa-check', CANCELED: 'fas fa-ban', REVIEWING: 'fas fa-search', NEW: 'fas fa-star', INQUIRY: 'fas fa-question' }
      return map[status] || 'fas fa-info-circle'
    },
    goToContract(id) {
      window.open(`/real_estate/contracts/rent/${id}`, '_blank');
    },
    openSideBar(id) {
      this.$refs.contractRentSidebar.showContract(id)
    },
    convertToEURO(someNumber) {
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber);
    },
    gradientColor(progress) {
      const hue = ((1 - progress / 100) * 120).toString(10);
      return `hsl(${hue}, 100%, 50%)`;
    },
    getEndDateColor(endDate) {
      let today = this.$moment();
      let diffDays = this.$moment(endDate).diff(today, 'days');
      if (diffDays < 365) return "danger"
      else if (diffDays < 730) return "warning"
      return "success"
    },
    onContractGenerate() {
      this.dialogs.contract = true
    },
    onTreasureContractGenerateMulti() {
      this.dialogs.treasureContractMulti = true
    },
    onBlockCalendar() {
      this.dialogs.blockCalendar = true
    },
    onTreasureContractGenerate() {
      this.dialogs.treasureContract = true
    },
    /** v-select does not trigger b-pagination @input — refetch when page size changes */
    onPerPageChange(value) {
      const n = typeof value === 'number' ? value : parseInt(String(value), 10)
      if (!Number.isNaN(n)) {
        this.page.amount = n
      }
      this.page.currentPage = 1
      this.refresh()
    },
    refresh() {
      let $this = this
      this.loading = true;
      if (this.datesRange != null) {
        this.filter.gte.moveDate = this.datesRange.start
        this.filter.lte.moveDate = this.datesRange.end
      } else {
        this.filter.gte.moveDate = null
        this.filter.lte.moveDate = null
      }
      this.filter.eq.over30Days = false;
      this.getAllContracts({
        page: $this.page.currentPage,
        amount: $this.page.amount,
        filter: $this.filter,
        sortcolumn: 'creationTime',
        sortby: 'desc',
      }).then(this.onContractsRetrieve, (error) => $this.error = error)
    },
    onContractsRetrieve(data) {
      this.page = {
        amount: data['Amount'],
        total: data['Total'],
        currentPage: data['Page']
      };
      this.sort = {
        by: data["sortColumn"],
        direction: data["sortOrder"]
      }
      this.contracts = data.contracts.filter(contract => contract.status !== "INQUIRY");
      this.realEstates = (data.realEstates || []).toObject("id");
      this.loading = false;
    },
    getImageSrc(property) {
      if (property == null || property.attachments == null || property.attachments[0] == null)
        return null
      return (property.attachments[0].attachmentUrl.startsWith('http') ? '' : this.getRealEstatesWebSite) + property.attachments[0].attachmentUrl;
    },
    onSort(data) {
      this.sort.by = data.sortBy;
      this.sort.direction = data.sortDesc ? "desc" : "asc";
      this.refresh();
    },
    onRowClick(contract) {
      window.open(`/real_estate/contracts/rent/${contract.id}`, '_blank');
    },
    onClientSelect(clientId) {
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('crm::client::select', clientId)
    },
    submitContract() {
      this.savingContract = true;
      this.$root.$emit('crm::submitContract')
    },
    checkAvailability() {
      this.$root.$emit('crm::checkContract')
    },
    resetButton() {
      this.savingContract = false;
    },
    onSubmitted(ans) {
      if (ans.status) {
        this.refresh()
        this.dialogs.contract = false;
        this.dialogs.treasureContract = false;
        this.dialogs.treasureContractMulti = false;
        this.dialogs.blockCalendar = false;
        this.savingContract = false;
      }
    },
    contractToast(ans) {
      console.log("contractToast : ", ans);
      if (ans == null) {
        this.$bvModal.msgBoxOk('No contracts on these dates', {
          title: 'No Contracts',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success'
        });
      } else {
        this.busyContracts = ans;
        console.log("busyContracts length : ", this.busyContracts.length);
        this.$refs.contractModal.show();
      }
    },
    sendCheckInDetailsButtonSMS(id) {
      this.sendSMSCheckIn(id);
    },
    getProgressValue(contract) {
      var sum = 100;
      let isSignedValue = contract.signedOffer ? 0 : 25;
      if (contract.alerts == null || contract.alerts.length === 0)
        return 100 - isSignedValue;
      for (let i = 0; i < contract.alerts.length; i++) {
        if (contract.alerts[i].actionRequired === "RENT_PAYMENT") {
          sum -= 25;
        } else if (contract.alerts[i].actionRequired === "AUTHORIZATION_FEE_PAYMENT") {
          sum -= 25;
        } else if (contract.alerts[i].actionRequired === "UPLOAD_REQUIRED_FILES") {
          sum -= 25;
        }
      }
      return sum - isSignedValue;
    },
  }
}
</script>

<style scoped>
.rc-page {
  padding-bottom: 20px;
  background: radial-gradient(1200px 400px at 20% 0%, rgba(115, 103, 240, .05), transparent 60%),
  radial-gradient(1000px 380px at 90% 10%, rgba(0, 184, 148, .05), transparent 55%);
  border-radius: 18px;
}

/* Action buttons */
.rc-btn { border-radius: 12px; font-weight: 700; font-size: 12px; }

/* Filters */
.rc-filter-wrap.sticky { position: sticky; z-index: 999; top: 80px; }
/* overflow:visible so v-calendar range popover is not clipped (popover uses fixed strategy + z-index in calendarPickerRange) */
.rc-filter-card { border-radius: 18px; overflow: visible; border: 1px solid rgba(0,0,0,.06); box-shadow: 0 8px 20px rgba(0,0,0,.05); }
.rc-filter-icon { color: #7367f0; font-size: 13px; }
.rc-filter-title { font-weight: 900; font-size: 13px; }
.rc-filter-action { color: inherit; opacity: .5; transition: opacity .12s; font-size: 13px; }
.rc-filter-action:hover { opacity: 1; }
.rc-pin { color: inherit; opacity: .35; transition: all .12s; }
.rc-pin-active { color: #7367f0; opacity: 1; }
.rc-filter-grid { row-gap: 6px; }
.rc-filter-col { margin-bottom: 4px; }
.rc-label { font-size: 11px; font-weight: 700; opacity: .7; margin-bottom: 2px; }
.rc-input { border-radius: 10px; }
.rc-select :deep(.vs__dropdown-toggle) { border-radius: 10px; }

/* Table Card */
.rc-table-card { border-radius: 18px; overflow: hidden; border: 1px solid rgba(0,0,0,.06); box-shadow: 0 10px 26px rgba(0,0,0,.06); }
.rc-page-select { width: 75px; min-width: 70px; }
.rc-page-select :deep(.vs__dropdown-toggle) {
  border-radius: 10px;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 0 6px !important;
}
.rc-page-select :deep(.vs__selected),
.rc-page-select :deep(.vs__search) {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.35 !important;
}
.rc-page-select :deep(.vs__actions) { padding: 0 4px 0 0 !important; }
.rc-total-badge { font-size: 12px; padding: 6px 14px; border-radius: 999px; background: rgba(115, 103, 240, .08); border: 1px solid rgba(115, 103, 240, .15); color: #7367f0; font-weight: 700; }

/* Table */
.rc-table :deep(thead) { background: rgba(0,0,0,.02); }
.rc-th { font-size: 11px !important; font-weight: 800 !important; text-transform: uppercase; letter-spacing: .5px; white-space: nowrap; padding: 10px 12px !important; }
.rc-th-center { text-align: center !important; }
.rc-td { padding: 10px 12px !important; vertical-align: middle !important; }
.rc-table :deep(tbody tr) { cursor: pointer; transition: background .12s; }
.rc-table :deep(tbody tr:hover) { background: rgba(115, 103, 240, .04) !important; }

/* Status badge */
.rc-status-badge { font-weight: 700; font-size: 11px; padding: 5px 12px; border-radius: 999px; }

/* View button */
.rc-view-btn { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: rgba(115, 103, 240, .08); border: 1px solid rgba(115, 103, 240, .15); color: #7367f0; transition: all .12s; }
.rc-view-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(115, 103, 240, .2); background: rgba(115, 103, 240, .14); color: #7367f0; }

/* Date */
.rc-date-text { font-size: 12px; font-weight: 600; white-space: nowrap; }

/* Property */
.rc-property-img { width: 42px; height: 42px; object-fit: cover; border-radius: 10px; flex-shrink: 0; }
.rc-property-text { min-width: 0; }
.rc-property-addr { font-weight: 800; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }
.rc-property-city { font-size: 11px; opacity: .6; }

/* Client */
.rc-client-cell { display: flex; align-items: center; }
.rc-client-icon { font-size: 18px; opacity: .4; }
.rc-client-name { font-weight: 800; font-size: 12px; white-space: nowrap; }
.rc-client-country { font-size: 11px; opacity: .6; }

/* Date pills */
.rc-date-pill { display: inline-flex; align-items: center; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
.rc-date-checkin { background: rgba(40, 199, 111, .12); border: 1px solid rgba(40, 199, 111, .22); color: #28c76f; }
.rc-date-danger { background: rgba(234, 84, 85, .12); border: 1px solid rgba(234, 84, 85, .22); color: #ea5455; }
.rc-date-warning { background: rgba(255, 159, 67, .12); border: 1px solid rgba(255, 159, 67, .22); color: #ff9f43; }
.rc-date-success { background: rgba(40, 199, 111, .12); border: 1px solid rgba(40, 199, 111, .22); color: #28c76f; }

/* Progress */
.rc-progress-wrap { display: flex; align-items: center; gap: 8px; min-width: 100px; }
.rc-progress { border-radius: 999px; flex: 1; }
.rc-progress :deep(.progress-bar) { border-radius: 999px; }
.rc-progress-label { font-size: 11px; font-weight: 800; white-space: nowrap; min-width: 30px; }

/* Price */
.rc-price { font-weight: 800; font-size: 13px; white-space: nowrap; }

/* Special requests */
.rc-special-badge { display: inline-flex; align-items: center; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
.rc-special-yes { background: rgba(40, 199, 111, .12); border: 1px solid rgba(40, 199, 111, .22); color: #28c76f; }
.rc-special-no { background: rgba(0,0,0,.04); border: 1px solid rgba(0,0,0,.08); color: rgba(0,0,0,.3); }

/* Check icons */
.rc-check-yes { font-size: 1.3rem; color: #28c76f; }
.rc-check-no { font-size: 1.3rem; color: #ea5455; opacity: .6; }

/* Pagination */
.rc-pagination :deep(.page-link) { border-radius: 10px !important; }
.rc-page-info { font-size: 12px; }

/* Modal */
.rc-modal-content { border-radius: 18px; overflow: hidden; }

/* Busy contracts list */
.rc-busy-list { list-style: none; padding: 0; margin: 0; }
.rc-busy-item { padding: 12px; border-radius: 14px; border: 1px solid rgba(0,0,0,.08); background: rgba(0,0,0,.01); margin-bottom: 10px; }
</style>
